import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Switch,
  Divider,
  Alert,
  Space,
  Row,
  Col,
  Tag
} from 'antd';
import { AudioOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../auth/AuthContext';
import useAppStore from '../../../context/AppStore';

const { Title, Text, Paragraph } = Typography;

const Dictation = () => {
  const auth = useAuth();
  const { setDictationEnabled } = useAppStore();
  const userDetails = localStorage.getItem('userDetails');
  const initialUser = userDetails ? JSON.parse(userDetails) : {};
  // Initialize once from localStorage
  const [isEnabled, setIsEnabled] = useState(initialUser.dictation_enabled || false);
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    // Determine if the user is on a Mac
    setIsMac(navigator.platform.toUpperCase().includes('MAC'));
  }, []);

  // Returns the shortcut text for starting dictation (Shift key for both OS)
  const getShortcutText = () => "Shift";
  // Returns the shortcut text for stopping dictation (Shift key again)
  const getStopShortcutText = () => "Shift";

  // Handle the toggle switch change with optimistic update
  const handleToggleChange = async (checked) => {
    // Optimistically update the UI immediately
    setIsEnabled(checked);
    try {
      const formData = new FormData();
      formData.append("enable_dictation", checked);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/update_dictation`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth.user['access_token']}`,
          'Accept': 'application/json',
          // Do not set 'Content-Type' header when sending FormData
        },
        body: formData
      });

      if (response.ok) {
        // Update localStorage so next page load reflects the new setting
        localStorage.setItem(
          'userDetails',
          JSON.stringify({ ...initialUser, dictation_enabled: checked })
        );
        // Update the store
        setDictationEnabled(checked);
      } else {
        console.error('Failed to update dictation setting');
        // Revert the toggle if the API call fails
        setIsEnabled(!checked);
      }
    } catch (error) {
      console.error('Error updating dictation setting:', error);
      // Revert the toggle on error
      setIsEnabled(!checked);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AudioOutlined style={{ fontSize: '24px', marginRight: '12px', color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>{"AI Dictation (Beta Mode)"}</Title>
          </div>
        }
        style={{ marginBottom: '20px' }}
      >
        <Row align="middle" style={{ marginBottom: '20px' }}>
          <Col span={16}>
            <div>
              <Title level={4} style={{ marginBottom: '8px' }}>Enable AI Dictation</Title>
              <Text type="secondary">
                This feature currently works to edit the medical note and interact with the Copilot. Enable it to use it via the key press described below.
              </Text>
            </div>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Switch 
              checked={isEnabled} 
              onChange={handleToggleChange} 
              checkedChildren="ON" 
              unCheckedChildren="OFF"
            />
          </Col>
        </Row>
        
        <Divider />
        
        {isEnabled ? (
          <>
            <div style={{ marginBottom: '20px' }}>
              <Title level={4}>Using Dictation</Title>
              <Paragraph>
                When dictation is enabled, simply press and hold the <strong>Shift</strong> key to start dictation and release it to stop. Your speech will then be processed to edit your medical note or interact with the Copilot.
              </Paragraph>
              
              <Card style={{ background: '#f5f5f5', marginBottom: '20px' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Tag color="blue" style={{ padding: '4px 8px', marginRight: '12px' }}>
                      Press {getShortcutText()} and Hold 
                    </Tag>
                    <Text strong>Start Dictation</Text>
                  </div>
                  <div>
                    <Tag color="red" style={{ padding: '4px 8px', marginRight: '12px' }}>
                      Release {getStopShortcutText()}
                    </Tag>
                    <Text strong>Stop Dictation</Text>
                  </div>
                </Space>
              </Card>
              
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Microphone Access Required"
                description={
                  <div>
                    <Paragraph>
                      Dictation requires microphone access. Your speech is processed through a secure AI system for accurate transcription.
                      {isMac && ' On Mac, make sure your browser has permission to access the microphone in System Settings > Privacy & Security.'}
                    </Paragraph>
                  </div>
                }
              />
            </div>
            
            <Divider />
            
            <div>
              <Title level={4}>Dictation Quality</Title>
              <Paragraph>
                The AI dictation feature uses advanced technology to ensure high-quality transcription.
                For best results:
              </Paragraph>
              <ul>
                <li>Speak clearly at a normal pace</li>
                <li>Minimize background noise</li>
                <li>Position yourself in a well-lit environment</li>
                <li>Use proper medical terminology when applicable</li>
              </ul>
            </div>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message="Dictation is currently disabled"
            description="Enable AI dictation to use voice input to edit medical note and interact with the Copilot."
          />
        )}
      </Card>
    </div>
  );
};

export default Dictation;
